import {
    Accordion,
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Center,
    Group,
    Loader,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { CityQuestStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-status.enum';
import { PartnerCityQuestStopDto } from '@routellers/types/dist/partners-portal/partner-city-quest.dto';
import { PortalCityOptionRO } from '@routellers/types/dist/partners-portal/portal-city-option.ro';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { RoutePoint } from '@routellers/utils/dist';
import {
    IconArrowLeft,
    IconInfoCircle,
    IconMapPin,
    IconPlus,
    IconSend,
    IconTrash,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { AttractionFormModal } from '../components/attraction-form-modal';
import { AttractionPicker } from '../components/attraction-picker';
import { RouteMap } from '../components/route-map';
import { RouteWarnings } from '../components/route-warnings';
import { StoryStatusBadge } from '../components/status-badge';
import { t } from '../dictionary';
import { fameLabel } from '../utils/attraction-labels';

const languageLabel = (code: string) => {
    try {
        return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? code;
    } catch {
        return code;
    }
};

const languageOptions = Object.values(Languages).map((code) => ({
    value: code,
    label: languageLabel(code),
}));

type EditorStep = {
    id?: string;
    title: string;
    attractionId: number;
    narrative: string;
    storyAfter: string;
    arrivalInstruction: string;
    riddle: string;
    answer: string;
    hint: string;
    fromPrevious?: string;
    walkMicroTask?: string;
};

const emptyStep = (): EditorStep => ({
    title: '',
    attractionId: 0,
    narrative: '',
    storyAfter: '',
    arrivalInstruction: '',
    riddle: '',
    answer: '',
    hint: '',
    fromPrevious: '',
    walkMicroTask: '',
});

const stopToEditor = (stop: PartnerCityQuestStopDto): EditorStep => ({
    id: stop.id,
    title: stop.title || '',
    attractionId: stop.attractionId || 0,
    narrative: stop.storyBefore || '',
    storyAfter: stop.storyAfter || '',
    arrivalInstruction: stop.arrivalInstruction || '',
    riddle: stop.riddle || '',
    answer: stop.answer || '',
    hint: stop.hint || '',
    fromPrevious: stop.fromPrevious || '',
    walkMicroTask: stop.walkMicroTask || '',
});

const editorToStop = (step: EditorStep, order: number): PartnerCityQuestStopDto => ({
    id: step.id,
    order,
    attractionId: step.attractionId,
    title: step.title.trim(),
    storyBefore: step.narrative.trim() || undefined,
    storyAfter: step.storyAfter.trim() || undefined,
    arrivalInstruction: step.arrivalInstruction.trim() || undefined,
    riddle: step.riddle.trim() || undefined,
    answer: step.answer.trim() || undefined,
    hint: step.hint.trim() || undefined,
    fromPrevious: step.fromPrevious?.trim() || undefined,
    walkMicroTask: step.walkMicroTask?.trim() || undefined,
});

const EDITABLE = [CityQuestStatusEnum.DRAFT, CityQuestStatusEnum.REJECTED];

const stepHasText = (step: EditorStep) =>
    [
        step.title,
        step.narrative,
        step.storyAfter,
        step.arrivalInstruction,
        step.riddle,
        step.answer,
        step.hint,
        step.fromPrevious,
        step.walkMicroTask,
    ].some((v) => !!v?.trim());

export const StoryEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id;

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState<CityQuestStatusEnum>(CityQuestStatusEnum.DRAFT);
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [cityId, setCityId] = useState<number | null>(null);
    const [cityTitle, setCityTitle] = useState<string | null>(null);
    const [citySearch, setCitySearch] = useState('');
    const [cities, setCities] = useState<PortalCityOptionRO[]>([]);
    const [language, setLanguage] = useState<string>(Languages.EN);
    const [steps, setSteps] = useState<EditorStep[]>([emptyStep()]);
    const [attractionsById, setAttractionsById] = useState<Record<number, CityAttractionRO>>({});

    const [pickerStepIndex, setPickerStepIndex] = useState<number | null>(null);
    const [createOpen, setCreateOpen] = useState(false);

    const cityLocked = steps.some((s) => s.attractionId > 0);
    const languageLocked = steps.some(stepHasText);
    const readOnly = !isNew && !EDITABLE.includes(status);

    useEffect(() => {
        const timer = setTimeout(() => {
            api.questCities(citySearch)
                .then(({ data }) => setCities(data))
                .catch(() => setCities([]));
        }, 250);

        return () => clearTimeout(timer);
    }, [citySearch]);

    useEffect(() => {
        if (isNew) return;

        api.quest(+id)
            .then(async ({ data }) => {
                setTitle(data.title?.trim() || '');
                setDescription(data.tagline || '');
                setCityId(data.cityId ?? null);
                setCityTitle(data.cityTitle ?? null);
                setLanguage(data.language || Languages.EN);
                setSteps(data.stops?.length ? data.stops.map(stopToEditor) : [emptyStep()]);
                setStatus(data.status);
                setRejectionReason(data.rejectionReason ?? null);

                if (data.cityId) {
                    try {
                        const { data: list } = await api.attractions({ cityId: data.cityId });
                        const map: Record<number, CityAttractionRO> = {};

                        for (const a of list) map[a.id] = a;
                        setAttractionsById(map);
                    } catch {
                        setAttractionsById({});
                    }
                }
            })
            .catch(() => navigate('/quests'))
            .finally(() => setLoading(false));
    }, [id, isNew, navigate]);

    const cityOptions = useMemo(() => {
        const options = cities.map((c) => ({
            value: String(c.id),
            label: `${c.title} (${c.country?.toUpperCase?.() || ''})`,
        }));

        if (cityId && cityTitle && !options.some((o) => o.value === String(cityId))) {
            options.unshift({ value: String(cityId), label: cityTitle });
        }

        return options;
    }, [cities, cityId, cityTitle]);

    const patchStep = (index: number, patch: Partial<EditorStep>) => {
        setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    const rememberAttraction = (attraction: CityAttractionRO) => {
        setAttractionsById((prev) => ({ ...prev, [attraction.id]: attraction }));
    };

    const assignAttraction = (index: number, attraction: CityAttractionRO) => {
        rememberAttraction(attraction);
        setSteps((prev) =>
            prev.map((s, i) =>
                i === index
                    ? {
                          ...s,
                          attractionId: attraction.id,
                          title: attraction.title,
                      }
                    : s,
            ),
        );
    };

    const routePoints: RoutePoint[] = useMemo(() => {
        const points: RoutePoint[] = [];

        for (const step of steps) {
            if (!step.attractionId) continue;
            const a = attractionsById[step.attractionId];

            if (a?.lat != null && a?.lng != null) {
                points.push({ lat: a.lat, lng: a.lng });
            }
        }

        return points;
    }, [steps, attractionsById]);

    const mapPoints = useMemo(
        () =>
            steps
                .map((step) => {
                    const a = step.attractionId ? attractionsById[step.attractionId] : undefined;

                    if (!a || a.lat == null || a.lng == null) return null;

                    return { lat: a.lat, lng: a.lng, label: step.title || a.title };
                })
                .filter(Boolean) as { lat: number; lng: number; label?: string }[],
        [steps, attractionsById],
    );

    const hasUnapproved = steps.some((s) => {
        if (!s.attractionId) return false;
        const a = attractionsById[s.attractionId];

        return !a || a.reviewStatus !== AttractionReviewStatusEnum.APPROVED;
    });

    const save = async (opts?: { forSubmit?: boolean }): Promise<number | null> => {
        if (!title.trim()) {
            notifications.show({ color: 'red', message: t.stories.titleRequired });

            return null;
        }

        if (!cityId) {
            notifications.show({ color: 'red', message: t.stories.cityRequired });

            return null;
        }

        const payloadSteps = steps
            .map((s, i) => ({ step: s, order: i + 1 }))
            .filter(({ step }) => step.title.trim() && step.attractionId > 0)
            .map(({ step, order }) => editorToStop(step, order));

        if (opts?.forSubmit && payloadSteps.length === 0) {
            notifications.show({ color: 'red', message: t.stories.needStops });

            return null;
        }

        if (opts?.forSubmit && payloadSteps.some((s) => !s.riddle?.trim() || !s.answer?.trim())) {
            notifications.show({ color: 'yellow', message: t.stories.needRiddles });
        }

        setSaving(true);

        try {
            const payload = {
                title,
                tagline: description.trim() || null,
                cityId: cityId!,
                language: language as Languages,
                stops: payloadSteps,
            };

            if (isNew) {
                const { data } = await api.createQuest(payload);

                return +data.id;
            }

            await api.updateQuest(+id, payload);

            return +id;
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });

            return null;
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async () => {
        const savedId = await save();

        if (!savedId) return;

        if (isNew) {
            navigate(`/quests/${savedId}`, { replace: true });

            return;
        }

        notifications.show({ color: 'green', message: t.common.saved });
    };

    const submitForReview = async () => {
        if (hasUnapproved) {
            notifications.show({ color: 'red', message: t.stories.unapprovedBlockSubmit });

            return;
        }

        const savedId = await save({ forSubmit: true });

        if (!savedId) return;

        setSubmitting(true);

        try {
            await api.submitQuest(savedId);
            notifications.show({ color: 'green', message: t.stories.submitted });
            navigate('/quests');
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSubmitting(false);
        }
    };

    const deleteStory = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(t.stories.deleteConfirm)) return;

        try {
            await api.deleteQuest(+id);
            navigate('/quests');
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    if (loading) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    const selectedCount = steps.filter((s) => s.attractionId > 0).length;

    return (
        <>
            <Group justify='space-between' mb='lg' wrap='wrap'>
                <Group gap='sm'>
                    <ActionIcon component={Link} to='/quests' variant='subtle' color='gray'>
                        <IconArrowLeft size={20} />
                    </ActionIcon>
                    <Title order={2}>
                        {isNew ? t.stories.editorTitleNew : t.stories.editorTitleEdit}
                    </Title>
                    {!isNew && <StoryStatusBadge status={status} />}
                </Group>

                {!readOnly && (
                    <Group gap='sm'>
                        {!isNew && (
                            <Tooltip label={t.common.delete}>
                                <ActionIcon variant='subtle' color='red' onClick={deleteStory}>
                                    <IconTrash size={18} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Button variant='default' loading={saving} onClick={saveDraft}>
                            {t.stories.saveDraft}
                        </Button>
                        <Button
                            leftSection={<IconSend size={16} />}
                            loading={submitting}
                            disabled={hasUnapproved}
                            onClick={submitForReview}
                        >
                            {t.stories.submit}
                        </Button>
                    </Group>
                )}
            </Group>

            <Stack gap='lg'>
                {readOnly && (
                    <Alert variant='light' color='indigo' icon={<IconInfoCircle />}>
                        {t.stories.readOnlyNotice}
                    </Alert>
                )}

                {status === CityQuestStatusEnum.REJECTED && rejectionReason && (
                    <Alert variant='light' color='red' title={t.stories.rejectionTitle}>
                        {rejectionReason}
                    </Alert>
                )}

                {!readOnly && (
                    <Accordion variant='contained' radius='md'>
                        <Accordion.Item value='guide'>
                            <Accordion.Control>
                                <Text fw={650}>{t.stories.guideTitle}</Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap='sm'>
                                    <Text size='sm' c='dimmed'>
                                        {t.stories.guideIntro}
                                    </Text>
                                    <Stack gap={6}>
                                        {t.stories.guideBullets.map((line) => (
                                            <Text key={line} size='sm'>
                                                • {line}
                                            </Text>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                )}

                <Card padding='lg'>
                    <Stack gap='md'>
                        <TextInput
                            label={t.stories.storyTitle}
                            description={t.stories.storyTitleHint}
                            placeholder={t.stories.storyTitlePlaceholder}
                            value={title}
                            disabled={readOnly}
                            onChange={(e) => setTitle(e.currentTarget.value)}
                        />

                        <Textarea
                            label={t.stories.description}
                            description={t.stories.descriptionHint}
                            placeholder={t.stories.descriptionPlaceholder}
                            minRows={3}
                            autosize
                            value={description}
                            disabled={readOnly}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                        />

                        <Group grow align='flex-start'>
                            <Select
                                label={t.stories.citySelect}
                                placeholder={t.stories.citySelectPlaceholder}
                                data={cityOptions}
                                value={cityId ? String(cityId) : null}
                                disabled={readOnly || cityLocked}
                                searchable
                                searchValue={citySearch}
                                onSearchChange={setCitySearch}
                                onChange={(v) => {
                                    const nextId = v ? +v : null;
                                    const city = cities.find((c) => c.id === nextId);

                                    setCityId(nextId);
                                    setCityTitle(city?.title ?? null);
                                    setAttractionsById({});
                                }}
                            />
                            <Select
                                label={t.stories.language}
                                description={t.stories.languageHint}
                                data={languageOptions}
                                value={language}
                                disabled={readOnly || languageLocked}
                                onChange={(v) => v && setLanguage(v)}
                                searchable
                            />
                        </Group>

                        {cityLocked && !readOnly && (
                            <Alert variant='light' color='gray' icon={<IconInfoCircle />}>
                                {t.stories.cityLockedNotice}
                            </Alert>
                        )}

                        {languageLocked && !readOnly && (
                            <Alert variant='light' color='gray' icon={<IconInfoCircle />}>
                                {t.stories.languageLockedNotice}
                            </Alert>
                        )}
                    </Stack>
                </Card>

                {!cityId ? (
                    <Alert variant='light' color='indigo' icon={<IconMapPin />}>
                        {t.stories.cityRequired}
                    </Alert>
                ) : (
                    <>
                        <Card padding='lg'>
                            <Text fw={650}>{t.stories.stepsTitle}</Text>
                            <Text size='sm' c='dimmed' mb='md'>
                                {t.stories.stepsSubtitle}
                            </Text>

                            <Accordion variant='separated' radius='md' defaultValue='step-0'>
                                {steps.map((step, index) => {
                                    const attraction = step.attractionId
                                        ? attractionsById[step.attractionId]
                                        : undefined;

                                    return (
                                        <Accordion.Item
                                            // biome-ignore lint/suspicious/noArrayIndexKey: у шагов нет стабильных id
                                            key={index}
                                            value={`step-${index}`}
                                        >
                                            <Accordion.Control>
                                                <Group gap='xs'>
                                                    <Text fw={600} size='sm' c='dimmed'>
                                                        {index + 1}.
                                                    </Text>
                                                    <Text fw={600} size='sm'>
                                                        {step.title ||
                                                            t.stories.stepTitlePlaceholder}
                                                    </Text>
                                                    {attraction?.reviewStatus ===
                                                        AttractionReviewStatusEnum.NEED_REVIEW && (
                                                        <Badge
                                                            size='xs'
                                                            variant='light'
                                                            color='yellow'
                                                        >
                                                            {t.stories.attractionNeedReview}
                                                        </Badge>
                                                    )}
                                                    {attraction?.reviewStatus ===
                                                        AttractionReviewStatusEnum.REJECTED && (
                                                        <Badge
                                                            size='xs'
                                                            variant='light'
                                                            color='red'
                                                        >
                                                            {t.stories.attractionRejected}
                                                        </Badge>
                                                    )}
                                                </Group>
                                            </Accordion.Control>
                                            <Accordion.Panel>
                                                <Stack gap='sm'>
                                                    <Card
                                                        padding='sm'
                                                        radius='md'
                                                        withBorder
                                                        bg='gray.0'
                                                    >
                                                        <Group
                                                            justify='space-between'
                                                            wrap='nowrap'
                                                            align='flex-start'
                                                        >
                                                            <div style={{ minWidth: 0 }}>
                                                                <Text size='xs' c='dimmed'>
                                                                    {t.stories.pickAttraction}
                                                                </Text>
                                                                {attraction ? (
                                                                    <>
                                                                        <Text
                                                                            fw={650}
                                                                            size='sm'
                                                                            truncate
                                                                        >
                                                                            {attraction.title}
                                                                        </Text>
                                                                        <Text size='xs' c='dimmed'>
                                                                            {[
                                                                                attraction.district,
                                                                                fameLabel(
                                                                                    attraction.fame,
                                                                                ),
                                                                            ]
                                                                                .filter(Boolean)
                                                                                .join(' · ')}
                                                                        </Text>
                                                                    </>
                                                                ) : (
                                                                    <Text size='sm' c='dimmed'>
                                                                        {
                                                                            t.stories
                                                                                .noAttractionSelected
                                                                        }
                                                                    </Text>
                                                                )}
                                                            </div>
                                                            {!readOnly && (
                                                                <Button
                                                                    size='compact-sm'
                                                                    variant='light'
                                                                    onClick={() =>
                                                                        setPickerStepIndex(index)
                                                                    }
                                                                >
                                                                    {attraction
                                                                        ? t.stories.changeAttraction
                                                                        : t.stories.pickAttraction}
                                                                </Button>
                                                            )}
                                                        </Group>
                                                    </Card>

                                                    <TextInput
                                                        label={t.stories.stepTitle}
                                                        description={t.stories.stepTitleHint}
                                                        placeholder={t.stories.stepTitlePlaceholder}
                                                        value={step.title}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                title: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.stories.stepFromPrevious}
                                                        description={t.stories.stepFromPreviousHint}
                                                        placeholder={
                                                            t.stories.stepFromPreviousPlaceholder
                                                        }
                                                        minRows={2}
                                                        autosize
                                                        value={step.fromPrevious || ''}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                fromPrevious: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.stories.stepWalkTask}
                                                        description={t.stories.stepWalkTaskHint}
                                                        placeholder={
                                                            t.stories.stepWalkTaskPlaceholder
                                                        }
                                                        minRows={2}
                                                        autosize
                                                        value={step.walkMicroTask || ''}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                walkMicroTask:
                                                                    e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.stories.stepNarrative}
                                                        description={t.stories.stepNarrativeHint}
                                                        placeholder={
                                                            t.stories.stepNarrativePlaceholder
                                                        }
                                                        minRows={2}
                                                        autosize
                                                        value={step.narrative}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                narrative: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.stories.stepArrival}
                                                        description={t.stories.stepArrivalHint}
                                                        placeholder={
                                                            t.stories.stepArrivalPlaceholder
                                                        }
                                                        minRows={2}
                                                        autosize
                                                        value={step.arrivalInstruction}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                arrivalInstruction:
                                                                    e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.stories.stepRiddle}
                                                        description={t.stories.stepRiddleHint}
                                                        placeholder={
                                                            t.stories.stepRiddlePlaceholder
                                                        }
                                                        minRows={2}
                                                        autosize
                                                        value={step.riddle}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                riddle: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Group grow>
                                                        <TextInput
                                                            label={t.stories.stepAnswer}
                                                            description={t.stories.stepAnswerHint}
                                                            placeholder={
                                                                t.stories.stepAnswerPlaceholder
                                                            }
                                                            value={step.answer}
                                                            disabled={readOnly}
                                                            onChange={(e) =>
                                                                patchStep(index, {
                                                                    answer: e.currentTarget.value,
                                                                })
                                                            }
                                                        />
                                                        <TextInput
                                                            label={t.stories.stepHint}
                                                            description={t.stories.stepHintHint}
                                                            placeholder={
                                                                t.stories.stepHintPlaceholder
                                                            }
                                                            value={step.hint}
                                                            disabled={readOnly}
                                                            onChange={(e) =>
                                                                patchStep(index, {
                                                                    hint: e.currentTarget.value,
                                                                })
                                                            }
                                                        />
                                                    </Group>

                                                    <Textarea
                                                        label={t.stories.stepStoryAfter}
                                                        description={t.stories.stepStoryAfterHint}
                                                        placeholder={
                                                            t.stories.stepStoryAfterPlaceholder
                                                        }
                                                        minRows={2}
                                                        autosize
                                                        value={step.storyAfter}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                storyAfter: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    {!readOnly && steps.length > 1 && (
                                                        <Group justify='flex-end'>
                                                            <Button
                                                                variant='subtle'
                                                                color='red'
                                                                size='compact-sm'
                                                                leftSection={
                                                                    <IconTrash size={14} />
                                                                }
                                                                onClick={() =>
                                                                    setSteps((prev) =>
                                                                        prev.filter(
                                                                            (_, i) => i !== index,
                                                                        ),
                                                                    )
                                                                }
                                                            >
                                                                {t.stories.removeStep}
                                                            </Button>
                                                        </Group>
                                                    )}
                                                </Stack>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>

                            {!readOnly && (
                                <Button
                                    variant='light'
                                    mt='md'
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => setSteps((prev) => [...prev, emptyStep()])}
                                >
                                    {t.stories.addStep}
                                </Button>
                            )}
                        </Card>

                        {selectedCount > 0 && (
                            <Card padding='lg'>
                                <Text fw={650} mb='sm'>
                                    {t.stories.routePreview}
                                </Text>
                                <Stack gap='md'>
                                    <RouteMap points={mapPoints} />
                                    <RouteWarnings
                                        points={routePoints}
                                        stepCount={selectedCount}
                                        hasUnapproved={hasUnapproved}
                                    />
                                </Stack>
                            </Card>
                        )}
                    </>
                )}
            </Stack>

            {cityId != null && (
                <>
                    <AttractionPicker
                        opened={pickerStepIndex != null && !createOpen}
                        onClose={() => setPickerStepIndex(null)}
                        cityId={cityId}
                        onSelect={(attraction) => {
                            if (pickerStepIndex == null) return;
                            assignAttraction(pickerStepIndex, attraction);
                        }}
                        onCreateNew={() => setCreateOpen(true)}
                    />
                    <AttractionFormModal
                        opened={createOpen}
                        onClose={() => setCreateOpen(false)}
                        cityId={cityId}
                        onCreated={(attraction) => {
                            if (pickerStepIndex == null) return;
                            assignAttraction(pickerStepIndex, attraction);
                            setPickerStepIndex(null);
                        }}
                    />
                </>
            )}
        </>
    );
};
